/*
 * TODO before removing cart-selling-plan-select:
 * - Move the subscription-goodie eligibility and add/remove logic here.
 * - Support the PDP selling_plan hidden input when this component is reused there.
 * - Restore the original cart snapshot if cart/add.js fails after cart/clear.js.
 * - Verify whether one cart/add.js request can replace the legacy 20-item batching.
 */
class SubscriptionWidget extends HTMLElement {
  connectedCallback() {
    this.cart = this.closest('cart-items, cart-drawer-items');
    this.cartDrawer = this.closest('cart-drawer');
    this.toggle = this.querySelector('[data-subscription-toggle]');
    this.planSelect = this.querySelector('[data-selling-plan-select]');
    this.planOptions = this.querySelector('[data-selling-plan-options]');
    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);

    if (!this.toggle || !this.planSelect || !this.planOptions) {
      return;
    }

    this.updatePlanOptions();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  onChange(event) {
    event.stopPropagation();

    if (event.target === this.toggle) {
      this.updatePlanOptions();

      if (!this.toggle.checked) {
        this.planSelect.value = 'null';

        if (this.hasSubscriptionItems()) {
          this.replaceSellingPlans(null);
        } else {
          this.persistPlanState(null, []);
        }
      }

      return;
    }

    if (event.target === this.planSelect && this.toggle.checked) {
      const planIndex = this.getPlanIndex();

      if (planIndex !== null) {
        this.replaceSellingPlans(planIndex);
      }
    }
  }

  getPlanIndex() {
    const planIndex = Number(this.planSelect.value);

    return Number.isInteger(planIndex) && planIndex >= 0 ? planIndex : null;
  }

  updatePlanOptions() {
    this.planOptions.hidden = !this.toggle.checked;
  }

  hasSubscriptionItems() {
    return Boolean(this.cart?.querySelector('[data-has-selling-plan]'));
  }

  async replaceSellingPlans(planIndex) {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    this.setLoadingState(true);

    try {
      this.cart ||= document.querySelector('cart-items, cart-drawer-items');

      if (!this.cart) {
        throw new Error('Cart element was not found.');
      }

      const itemBatches = this.createItemBatches(planIndex);
      this.persistPlanState(planIndex, itemBatches);

      let cartState = await this.requestCartUpdate('cart/clear.js', {});

      for (const items of itemBatches) {
        cartState = await this.requestCartUpdate('cart/add.js', { items });
      }

      if (cartState?.sections) {
        this.cart.renderContents(cartState);
      }

      document.dispatchEvent(new CustomEvent('selling-plan::changed'));
    } catch (error) {
      console.error('Unable to update cart selling plans:', error);
    } finally {
      this.isUpdating = false;
      this.setLoadingState(false);
    }
  }

  createItemBatches(planIndex) {
    const items = Array.from(this.cart.querySelectorAll('.cart-item'))
      .map((cartItem) => this.createCartItem(cartItem, planIndex))
      .filter(Boolean)
      .reverse();

    return this.splitIntoBatches(items, 20);
  }

  createCartItem(cartItem, planIndex) {
    const variantId = cartItem.dataset.variantId;

    if (!variantId) {
      return null;
    }

    const specialProduct = cartItem.querySelector('special-product');

    if (specialProduct && !specialProduct.querySelector('input[type="checkbox"]')?.checked) {
      return null;
    }

    const quantityInput = cartItem.querySelector('quantity-popover input[type="number"]');
    const quantity = Number(quantityInput?.value) || 1;
    const item = { id: variantId, quantity };
    const properties = this.parseProperties(cartItem.dataset.properties);

    if (properties) {
      item.properties = properties;
    }

    if (!specialProduct && planIndex !== null) {
      const sellingPlanId = this.getSellingPlanId(cartItem, planIndex);

      if (sellingPlanId) {
        item.selling_plan = sellingPlanId;
      }
    }

    return item;
  }

  getSellingPlanId(cartItem, planIndex) {
    return cartItem.dataset.sellingPlanIds
      ?.split(',')
      .filter(Boolean)[planIndex];
  }

  parseProperties(properties) {
    if (!properties?.trim()) {
      return null;
    }

    try {
      const parsedProperties = JSON.parse(properties);

      return parsedProperties && typeof parsedProperties === 'object' && !Array.isArray(parsedProperties)
        ? parsedProperties
        : null;
    } catch (error) {
      console.error('Unable to parse cart item properties:', error);
      return null;
    }
  }

  splitIntoBatches(items, batchSize) {
    return Array.from({ length: Math.ceil(items.length / batchSize) }, (_, index) =>
      items.slice(index * batchSize, (index + 1) * batchSize)
    );
  }

  async requestCartUpdate(endpoint, body) {
    const sections = this.cart.getSectionsToRender().map((section) => section.section || section.id);
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...body,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const cartState = await response.json();

    if (!response.ok || cartState.errors) {
      throw new Error(cartState.description || 'Cart update failed.');
    }

    return cartState;
  }

  persistPlanState(planIndex, itemBatches) {
    try {
      window.sessionStorage.setItem('add-selling-plan-arrays', JSON.stringify(itemBatches));
    } catch (error) {
      console.warn('Unable to persist selling plan cart items:', error);
    }

    const root = window.Shopify?.routes?.root || '/';
    const cookiePath = root.endsWith('/') ? root : `${root}/`;

    if (planIndex === null) {
      document.cookie = `active_selling_plan=; path=${cookiePath}; max-age=0; SameSite=Lax`;
      return;
    }

    document.cookie = `active_selling_plan=${planIndex}; path=${cookiePath}; max-age=86400; SameSite=Lax`;
  }

  setLoadingState(isLoading) {
    this.setAttribute('aria-busy', String(isLoading));
    this.toggle.disabled = isLoading;
    this.planSelect.disabled = isLoading;
    this.cartDrawer?.classList.toggle('is-updating-subscription', isLoading);
    this.cartDrawer?.setAttribute('aria-busy', String(isLoading));
  }
}

if (!customElements.get('subscription-widget')) {
  customElements.define('subscription-widget', SubscriptionWidget);
}
